<template>
  <div class="flex flex-col info-container full-width" data-cy="customer-info">
    <h2
      class="d-flex align-items-center user-name mb-2"
      :class="{ 'page-header-1': !job, 'page-header-2': job }"
    >
      <span v-if="customer.parent">
        <LinkComponent
          :to="parentUrl"
          :text="customer.parent.name"
        />&nbsp;>&nbsp;
      </span>
      <LinkComponent
        v-if="job"
        :to="userShowPath(customer.id)"
        :text="headerName"
        data-cy="customer-header-link"
      />
      <span v-else data-cy="customer-header-name">
        {{ headerName }}
      </span>
      <img
        v-if="customer.membership"
        :src="customer.membership.icon_url"
        alt="Membership Icon"
        class="ml-1 member-icon"
      />
      <i
        v-if="showEditButton"
        class="far fa-pen-to-square link edit-icon ml-2"
        data-cy="edit-customer-profile"
        @click="emit('edit-profile')"
      />
    </h2>
    <div class="flex gap-4">
      <div
        class="info-cards w-full text-xs grid grid-cols-1 gap-4"
        :class="gridColumnClasses"
      >
        <AddressCard
          v-if="job"
          :address="serviceAddress"
          label="Service Address"
        />
        <AddressCard
          :address="billingAddress"
          :label="billingAddressLabel"
          :name="billingAddressName"
          icon="fa-dollar"
        />
        <EmailListCard
          :emails="customer.emails"
          @edit-profile="emit('edit-profile')"
        />
        <PhoneListCard
          :phones="customer.phones"
          :call-source="job ? 'job' : 'customer'"
          :call-source-id="job ? job.id : customer.id"
          @edit-profile="emit('edit-profile')"
        />
        <TagListCard
          v-if="!job"
          :customer="customer"
          @edit-profile="emit('edit-profile')"
        />
        <BalanceDueCard v-if="balanceDue" :balance-due="balanceDue" />
        <div
          v-if="!hideBadges"
          class="badge-container flex flex-col gap-3 relative rounded-lg"
        >
          <div class="badges flex flex-col gap-3">
            <AccountingKeyBadge
              :customer="customer"
              @save-success="emit('fetch-user')"
            />
            <MembershipBadge
              :customer="customer"
              @save-success="emit('fetch-user')"
            />
            <ParentBadge
              :customer="customer"
              @save-success="emit('fetch-user')"
            />
            <PaymentMethodBadge
              v-if="!billingDelegatedToParent"
              :customer="customer"
              @save-success="emit('fetch-user')"
            />
            <LastLoginBadge
              v-if="!billingDelegatedToParent"
              :customer="customer"
            />
          </div>
          <PasswordResetLink
            v-if="!billingDelegatedToParent"
            class="resetLink"
            :customer="customer"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, reactive } from "vue";
import { customerHeaderName } from "@/lib/customerTypes";

import LinkComponent from "@/components/Link.vue";
import AddressCard from "@/Customer/AddressCard.vue";
import ParentBadge from "@/Customer/Profile/ParentBadge.vue";
import { fullName } from "@/mixins";
import { subCustomersPath } from "@/mixins/path_helpers";
import { Customer } from "@/typings/customer";
import { Job } from "@/typings/job";
import EmailListCard from "./EmailListCard.vue";
import PhoneListCard from "./PhoneListCard.vue";
import AccountingKeyBadge from "./Profile/AccountingKeyBadge.vue";
import BalanceDueCard from "./Profile/BalanceDueCard.vue";
import LastLoginBadge from "./Profile/LastLoginBadge.vue";
import MembershipBadge from "./Profile/MembershipBadge.vue";
import PasswordResetLink from "./Profile/PasswordResetLink.vue";
import PaymentMethodBadge from "./Profile/PaymentMethodBadge.vue";
import TagListCard from "./TagListCard.vue";

const props = defineProps({
  hideBadges: {
    type: Boolean,
    default: false
  },
  showEditButton: {
    type: Boolean,
    default: false
  },
  job: {
    type: Object as () => Job,
    default: null
  },
  customer: {
    type: Object as () => Customer,
    default: null
  }
});

const emit = defineEmits(["edit-profile", "fetch-user", "refresh-tax-info"]);

// A business leads with its company and keeps the contact in brackets; a
// customer with no company reads exactly as it did before.
const headerName = computed(() =>
  customerHeaderName(
    fullName(props.customer),
    props.customer?.company_name,
    props.customer?.customer_type
  )
);

const serviceAddress = computed(() => {
  const job = props.job;
  const typedJob = job ? new Job(job) : null;

  return typedJob ? typedJob.service_address : null;
});

const billingAddress = computed(() => {
  const customer = props.customer;
  const typedCustomer = customer ? new Customer(customer) : null;

  return typedCustomer?.billing_address;
});

const billingAddressLabel = computed(() => {
  const customer = props.customer;
  if (!customer) {
    return null;
  }

  const typedCustomer: Customer = new Customer(customer);
  return typedCustomer.delegate_billing ? "Bills To" : "Billing Address";
});

const billingAddressName = computed(() => {
  const customer = props.customer;
  if (!customer) {
    return null;
  }

  const typedCustomer: Customer = new Customer(customer);

  if (!typedCustomer.delegate_billing) {
    return null;
  }

  return typedCustomer.parent?.name;
});

const billingDelegatedToParent = computed(() => {
  const customer = props.customer;
  if (!customer) {
    return false;
  }

  const typedCustomer: Customer = new Customer(customer);
  return typedCustomer.delegate_billing;
});

// Only the profile header shows the card; the job header and new-customer page share this component.
const balanceDue = computed(() => {
  if (props.job || props.hideBadges) return null;

  return props.customer?.balance_due ?? null;
});

const gridColumnClasses = computed(() => {
  if (props.job) return "lg:grid-cols-2";

  const xlColumns = balanceDue.value ? "xl:grid-cols-6" : "xl:grid-cols-5";
  return `md:grid-cols-2 lg:grid-cols-3 ${xlColumns}`;
});

const parentUrl = computed(() => {
  return subCustomersPath(props.customer.parent?.id);
});
</script>

<style lang="scss" scoped>
.user-name {
  font-style: normal;
  font-weight: 700;
}

.info-cards {
  display: flex;
  gap: 0.75rem;
  list-style: none;
}

.edit-icon {
  font-size: 16px;
  position: relative;
}

.member-icon {
  height: 1em;
  margin-bottom: 5px;
}

.badge-container {
  font-size: 0.75rem;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: var(--light-gray);
  .badges {
    flex-wrap: wrap;
  }
  .resetLink {
    white-space: nowrap;
  }
}
:deep(.simple-table) {
  overflow: hidden !important;
}
</style>
